import { Component, computed, OnDestroy, OnInit, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { AlertComponent } from '../../../../../common/alert/alert.component';
import { ContentComponent } from '../../../../../components/content/content.component';
import { AccordionGroupComponent } from '../../../../../common/accordion-group/accordion-group.component';
import { AccordionItemComponent } from '../../../../../common/accordion-item/accordion-item.component';
import { TabPanelComponent } from '../../../../../common/tab-view/tab-panel.component';
import { TabViewComponent } from '../../../../../common/tab-view/tab-view.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  selectApplication,
  selectEntityTypes,
  selectLoading,
  selectPending
} from '../../../../../store/selectors/application.selectors';
import { Store } from '@ngrx/store';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { EntityLabelPipe } from '../../../../../pipes/applicant-label.pipe';
import {
  ApplicationAppEntityApplicationRead,
  ContractorAppEntityApplicationRead,
  ContractorAppEntityContractorWrite,
  EntityTypeAppEntityDictionaryEntityType
} from '../../../../../openapi';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  loadEntityTypes,
  updateContractors
} from '../../../../../store/actions/application.actions';
import { ContractorItemForm } from './forms/contractor-item-form';
import {
  ContractorNaturalPersonFormComponent
} from './components/contractor-natural-person-form/contractor-natural-person-form.component';
import { ContractorCompanyFormComponent } from './components/contractor-company-form/contractor-company-form.component';
import { ReactiveFormsModule } from '@angular/forms';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { ContractorDataForm } from './forms/contractor-data-form';
import { ContractorsPreviewComponent } from './components/contractors-preview/contractors-preview.component';

export type ContractorTag = 'investor' | 'performer';

export interface ContractorComponentCustomTitles {
  sectionTitle: string;
  tag?: ContractorTag;
  dataSameAsApplicantLabels: {
    acceptLabel: string;
    rejectLabel: string;
  };
  cardTitle: string;
  cardTitle2: string;
  addBtnTitle: string;
  addBtn2Title: string;
  noDataInfo: string;
}

export interface ContractorDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  customTitles?: ContractorComponentCustomTitles;
}

@Component({
  selector: 'app-contractor-data-section',
  standalone: true,
  imports: [
    CommonModule,
    AlertComponent,
    ContentComponent,
    AccordionGroupComponent,
    AccordionItemComponent,
    TabPanelComponent,
    TabViewComponent,
    SpinnerComponent,
    RouterLink,
    StagesComponent,
    EntityLabelPipe,
    ContractorNaturalPersonFormComponent,
    ContractorCompanyFormComponent,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    ContractorsPreviewComponent
  ],
  providers: [ContractorItemForm, ContractorDataForm],
  templateUrl: './contractor-data-section.component.html'
})
export class ContractorDataSectionComponent implements OnInit, OnDestroy {

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public stages: string[] = this.route.snapshot.data['stages'];

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public showItemForm: boolean = false;

  public entityTypes: Signal<EntityTypeAppEntityDictionaryEntityType[]> = this.store$.selectSignal(selectEntityTypes);

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  private fetchApplication: Subscription = toObservable(this.application).subscribe(app => {
    if (app) {
      this.form.patchFormValues(app, this.tag);
      const contractors: ContractorAppEntityContractorWrite[] = (this.tag ? app?.contractors?.filter(v => v?.tag === this.tag) : app?.contractors)?.map((contractor: ContractorAppEntityApplicationRead) => ({
          entityType: contractor.entityType?.id || '',
          firstName: contractor?.firstName || '',
          lastName: contractor?.lastName || '',
          name: contractor?.name || '',
          nip: contractor?.nip || '',
          city: contractor.city,
          email: contractor.email,
          apartmentNumber: contractor.apartmentNumber,
          houseNumber: contractor.houseNumber,
          phoneNumber: contractor.phoneNumber,
          phoneNumber2: contractor.phoneNumber2,
          postalCode: contractor.postalCode,
          postOffice: contractor.postOffice,
          street: contractor.street
        })
      ) || [];
      this.form.contractors.setValue(contractors);
    }
  });

  public currentIndex: WritableSignal<number | undefined> = signal(undefined);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public customTitles: ContractorComponentCustomTitles | undefined = this.route.snapshot.data['customTitles'];

  public tag: ContractorTag | undefined = this.customTitles?.tag;

  @ViewChild('tabViewComponent', { static: false })
  tabViewComponent: TabViewComponent | undefined;

  constructor(
    public itemForm: ContractorItemForm,
    public form: ContractorDataForm,
    private router: Router,
    private route: ActivatedRoute,
    private store$: Store
  ) {
  }

  public onAdd(): void {
    this.showItemForm = true;
    this.currentIndex.set(undefined);
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public submitAndGoNext(): void {
    const currentApp = this.application();
    if (this.form.valid && currentApp) {
      this.store$.dispatch(updateContractors({
        application: currentApp,
        payload: this.form.toWriteModel(this.tag),
        contractors: this.form.contractorsValues,
        successFnc: (applicationId: string) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        },
        tag: this.tag
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public onUpdateContractors(contractor: ContractorAppEntityContractorWrite): void {
    if (this.currentIndex() !== undefined) {
      this.form.editContractorItem(this.currentIndex() || 0, contractor);
      this.currentIndex.set(undefined);
    } else {
      this.form.addContractorItem(contractor);
    }
    this.showItemForm = false;
    this.itemForm.reset();
  }

  public onEditContractor(i: number, contractor: ContractorAppEntityContractorWrite): void {
    const tabName: string = this.entityTypes()?.filter(v => v.id === contractor?.entityType)[0]?.name || '';
    if (contractor && tabName) {
      this.currentIndex.set(i);
      this.showItemForm = true;

      setTimeout(() => {

        this.tabViewComponent?.onNavigate(this.tabViewComponent?.panels.filter(v => v?.name === tabName)[0]);
        this.tabViewComponent?.ref.detectChanges();

        this.itemForm.patchValue({
          ...contractor,
          zipCode: contractor?.postalCode,
          buildingNumber: contractor?.houseNumber,
          phone: contractor?.phoneNumber,
          phone2: contractor?.phoneNumber2
        });
      });
    }
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadEntityTypes());
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }
}
